import {
	IGetMfaSessionAllIssuesParams,
	IMfaSessionDetails,
	TMfaSessionIssuesFilterOptions
} from 'src/apps/mfa/services/MfaDashboardServiceTypes';
import { TMfaSessionAllIssuesResult } from 'src/apps/mfa/types/MfaDashboardServiceTypes';
import { SummaryPaneResult } from 'src/apps/mobile-common/types/CommonTypes';
import { ISeverityCountType } from 'src/common/components/mobile-scans-impacted-by-severity-chart/MobileScansImpactedBySeverityChart';
import { gqlClient } from 'src/common/services/api/GraphQLRequestApi';
import { Logger } from 'src/common/services/Logger';
import {
	MobileIssueDetails,
	MobileIssueTypeCount
} from 'src/common/types/MobileSingleSessionTypes';

import { buildMfaIssueVariables } from './queries/issue/helpers/buildVariables';
import { IMfaIssueData, transformMfaIssueResults } from './queries/issue/helpers/transformResults';
import { MfaIssueQuery } from './queries/issue/MfaIssue.generated';
import { MfaIssue } from './queries/issue/MfaIssue.gql';
import { buildMfaIssueDetailsVariables } from './queries/issue-details/helpers/buildVariables';
import { transformMfaIssueDetailsResults } from './queries/issue-details/helpers/transformResults';
import { MfaIssueDetailsQuery } from './queries/issue-details/MfaIssueDetails.generated';
import { MfaIssueDetails } from './queries/issue-details/MfaIssueDetails.gql';
import { buildMfaSessionDetailsQueryVariables } from './queries/session-details/helpers/buildVariables';
import { transformMfaSessionDetailsResults } from './queries/session-details/helpers/transformResults';
import { MfaSessionDetailsQuery } from './queries/session-details/MfaSessionDetails.generated';
import { MfaSessionDetails } from './queries/session-details/MfaSessionDetails.gql';
import { buildMfaSessionIssueTypesQueryVariables } from './queries/session-issue-types/helpers/buildVariables';
import { transformMfaSessionIssueTypesResults } from './queries/session-issue-types/helpers/transformResults';
import { MfaSessionIssueTypesQuery } from './queries/session-issue-types/MfaSessionIssueTypes.generated';
import { MfaSessionIssueTypes } from './queries/session-issue-types/MfaSessionIssueTypes.gql';
import { buildMfaSessionIssuesVariables } from './queries/session-issues/helpers/buildVariables';
import { transformMfaSessionIssuesResults } from './queries/session-issues/helpers/transformResults';
import { MfaSessionIssuesQuery } from './queries/session-issues/MfaSessionIssues.generated';
import { MfaSessionIssues } from './queries/session-issues/MfaSessionIssues.gql';
import { buildMfaSessionIssuesFilterOptionsVariables } from './queries/session-issues-filter-options/helpers/buildVariables';
import { transformMfaSessionIssuesFilterOptionsResults } from './queries/session-issues-filter-options/helpers/transformResults';
import { MfaSessionIssuesFilterOptionsQuery } from './queries/session-issues-filter-options/MfaSessionIssuesFilterOptions.generated';
import { MfaSessionIssuesFilterOptions } from './queries/session-issues-filter-options/MfaSessionIssuesFilterOptions.gql';
import {
	buildMfaSessionScansVariables,
	IGetMfaSessionScansParams
} from './queries/session-scans/helpers/buildVariables';
import { transformMfaSessionScansResults } from './queries/session-scans/helpers/transformResults';
import { MfaSessionScansQuery } from './queries/session-scans/MfaSessionScans.generated';
import { MfaSessionScans } from './queries/session-scans/MfaSessionScans.gql';
import { buildMfaSessionSeveritiesQueryVariables } from './queries/session-severities/helpers/buildVariables';
import { transformMfaSessionSeveritiesResults } from './queries/session-severities/helpers/transformResults';
import { MfaSessionSeveritiesQuery } from './queries/session-severities/MfaSessionSeverities.generated';
import { MfaSessionSeverities } from './queries/session-severities/MfaSessionSeverities.gql';
import { buildMfaSessionSummaryQueryVariables } from './queries/session-summary/helpers/buildVariables';
import { transformMfaSessionSummaryResults } from './queries/session-summary/helpers/transformResults';
import { MfaSessionSummaryQuery } from './queries/session-summary/MfaSessionSummary.generated';
import { MfaSessionSummary } from './queries/session-summary/MfaSessionSummary.gql';

export const getMfaSessionAllIssues = async (
	params: IGetMfaSessionAllIssuesParams
): Promise<TMfaSessionAllIssuesResult> => {
	try {
		const variables = buildMfaSessionIssuesVariables(params);
		const result = await gqlClient.request<MfaSessionIssuesQuery>(MfaSessionIssues, variables);

		return transformMfaSessionIssuesResults(result);
	} catch (error) {
		Logger.error('MfaDashboardService -> getMfaSessionAllIssues failed with an error:', error);
		throw error;
	}
};

export const getMfaSessionDetails = async (sessionId: string): Promise<IMfaSessionDetails> => {
	try {
		const variables = buildMfaSessionDetailsQueryVariables(sessionId);
		const result = await gqlClient.request<MfaSessionDetailsQuery>(MfaSessionDetails, variables);

		return transformMfaSessionDetailsResults(result);
	} catch (e) {
		Logger.error('MfaDashboardService -> getMfaSessionDetails failed with an error:', e);
		throw e;
	}
};

export const getMfaSessionIssuesFilterOptions = async (
	sessionId: string
): Promise<TMfaSessionIssuesFilterOptions> => {
	try {
		const variables = buildMfaSessionIssuesFilterOptionsVariables(sessionId);
		const result = await gqlClient.request<MfaSessionIssuesFilterOptionsQuery>(
			MfaSessionIssuesFilterOptions,
			variables
		);

		return transformMfaSessionIssuesFilterOptionsResults(result);
	} catch (e) {
		Logger.error(
			'MfaDashboardService -> getMfaSessionIssuesFilterOptions failed with an error:',
			e
		);
		throw e;
	}
};

export const getSessionSeverities = async ({
	sessionId
}: {
	sessionId: string;
}): Promise<ISeverityCountType[]> => {
	try {
		const variables = buildMfaSessionSeveritiesQueryVariables(sessionId);
		const result = await gqlClient.request<MfaSessionSeveritiesQuery>(
			MfaSessionSeverities,
			variables
		);

		return transformMfaSessionSeveritiesResults(result);
	} catch (e) {
		Logger.error('MfaDashboardService -> getMfaSessionSeverities failed with an error:', e);
		throw e;
	}
};

export const getSessionSummary = async ({
	sessionId
}: {
	sessionId: string;
}): Promise<SummaryPaneResult> => {
	try {
		const variables = buildMfaSessionSummaryQueryVariables(sessionId);
		const result = await gqlClient.request<MfaSessionSummaryQuery>(MfaSessionSummary, variables);

		return transformMfaSessionSummaryResults(result);
	} catch (e) {
		Logger.error('MfaDashboardService -> getSessionSummary failed with an error:', e);
		throw e;
	}
};

export const getMfaIssueDetails = async (
	issueId: string,
	runId: string
): Promise<MobileIssueDetails> => {
	try {
		const variables = buildMfaIssueDetailsVariables(issueId, runId);
		const result = await gqlClient.request<MfaIssueDetailsQuery>(MfaIssueDetails, variables);

		return transformMfaIssueDetailsResults(result);
	} catch (e) {
		Logger.error('MfaSingleSessionService -> getMfaIssueDetails failed with an error:', e);
		throw e;
	}
};

export const getMfaIssue = async (
	issueId: string,
	runId: string
): Promise<IMfaIssueData | null> => {
	try {
		const variables = buildMfaIssueVariables(issueId, runId);
		const result = await gqlClient.request<MfaIssueQuery>(MfaIssue, variables);

		return transformMfaIssueResults(result);
	} catch (e) {
		Logger.error('MfaSingleSessionService -> getMfaIssue failed with an error:', e);
		throw e;
	}
};

export const getSessionIssueTypes = async ({
	sessionId
}: {
	sessionId: string;
}): Promise<MobileIssueTypeCount[]> => {
	try {
		const variables = buildMfaSessionIssueTypesQueryVariables(sessionId);
		const result = await gqlClient.request<MfaSessionIssueTypesQuery>(
			MfaSessionIssueTypes,
			variables
		);

		return transformMfaSessionIssueTypesResults(result);
	} catch (e) {
		Logger.error('MfaDashboardService -> getSessionIssueTypes failed with an error:', e);
		throw e;
	}
};

export const getMfaSessionScans = async (
	params: IGetMfaSessionScansParams
): Promise<Map<string, string>> => {
	try {
		const variables = buildMfaSessionScansVariables(params);
		const result = await gqlClient.request<MfaSessionScansQuery>(MfaSessionScans, variables);

		return transformMfaSessionScansResults(result);
	} catch (error) {
		Logger.error('MfaSingleSessionService -> getMfaSessionScans failed with an error:', error);
		throw error;
	}
};
